import SvgIcon, {type SvgIconProps} from '@mui/material/SvgIcon';

export type FsStatementKind = 'balance-sheet' | 'profit-loss' | 'cash-flow';

type FsStatementIconProps = Omit<SvgIconProps, 'children'> & {
    kind: FsStatementKind;
    /** Pixel size shortcut used by existing tabs (`size={24}`). Maps to `sx.fontSize`. */
    size?: number;
    alt?: string;
    /** @deprecated Selection follows MUI Tab / parent color; kept for call-site compat. */
    selected?: boolean;
};

const ALT: Record<FsStatementKind, string> = {
    'balance-sheet': 'Balance Sheet',
    'profit-loss': 'Income Statement',
    'cash-flow': 'Cash Flow',
};

/** MUI Outlined optical weight on 24�24 artboard. */
const SW = 1.5;

/**
 * Custom FS icons in MUI Outlined style:
 * - viewBox 0 0 24 24
 * - stroke = currentColor (inherits Tab / theme color)
 * - fill none (no solid fills)
 * - strokeLinecap / strokeLinejoin round
 */
function OutlinedPaths({kind}: {kind: FsStatementKind}) {
    const common = {
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: SW,
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
    };

    if (kind === 'balance-sheet') {
        return (
            <g {...common}>
                <rect x="5" y="2.5" width="14" height="19" rx="1.25" />
                <path d="M8 6.5h8" />
                <path d="M8 9h6" />
                <path d="M8 11.5h4" />
                <path d="M7.5 15.5h9" />
                <circle cx="12" cy="15.5" r="1.1" />
                <path d="M8.5 15.5v2.5" />
                <path d="M15.5 15.5v2.5" />
                <path d="M7 18h3" />
                <path d="M14 18h3" />
            </g>
        );
    }

    if (kind === 'profit-loss') {
        return (
            <g {...common}>
                <rect x="5" y="3" width="14" height="18" rx="1.25" />
                <path d="M8.5 17V13" />
                <path d="M12 17V10" />
                <path d="M15.5 17V7" />
                <path d="M7.5 17.5h9" />
            </g>
        );
    }

    return (
        <g {...common}>
            <rect x="5" y="3" width="14" height="18" rx="1.25" />
            <path d="M9 11c0-2.2 1.8-4 4-4h1" />
            <path d="M12.5 5.5l1.5 1.5-1.5 1.5" />
            <path d="M15 14c0 2.2-1.8 4-4 4h-1" />
            <path d="M11.5 19.5l-1.5-1.5 1.5-1.5" />
            <circle cx="12" cy="12.5" r="1" />
        </g>
    );
}

function toOutlinedSvgIcon(
    kind: FsStatementKind,
    props: SvgIconProps & {size?: number; alt?: string},
) {
    const {size, alt, sx, fontSize = 'medium', ...rest} = props;
    const label = alt ?? ALT[kind];
    return (
        <SvgIcon
            viewBox="0 0 24 24"
            inheritViewBox={false}
            aria-label={label}
            fontSize={size != null ? undefined : fontSize}
            sx={[
                size != null ? {fontSize: size} : null,
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
            {...rest}
        >
            <OutlinedPaths kind={kind} />
        </SvgIcon>
    );
}

/** Named Outlined aliases � same pattern as `@mui/icons-material/*Outlined`. */
export function BalanceSheetOutlinedIcon(props: SvgIconProps & {size?: number; alt?: string}) {
    return toOutlinedSvgIcon('balance-sheet', props);
}

export function ProfitLossOutlinedIcon(props: SvgIconProps & {size?: number; alt?: string}) {
    return toOutlinedSvgIcon('profit-loss', props);
}

export function CashFlowOutlinedIcon(props: SvgIconProps & {size?: number; alt?: string}) {
    return toOutlinedSvgIcon('cash-flow', props);
}

/**
 * Kind-based wrapper used by FS tabs. Prefer named `*OutlinedIcon` exports for new call sites.
 */
export function FsStatementIcon(props: FsStatementIconProps) {
    const {kind, selected: _selected, ...rest} = props;
    return toOutlinedSvgIcon(kind, rest);
}

export default FsStatementIcon;
